// SCR-STAFF-201 تب عمومی (US-CAT-002): نام، نام کوتاه، دسته، نوع، واحد، تخصص‌ها (داده مرجع نمونه با نشان «نمونه»)، توضیح داخلی؛
// کد پس از نخستین فعال‌سازی فقط‌خواندنی (BR-CAT-002).
import { zodResolver } from '@hookform/resolvers/zod';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import { unwrap, type ServiceType, type ServiceUnit, type ServiceView } from '@phoenix/api-client';
import { useTenantSession, useWriteAccess } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import {
  applyServerErrors,
  ErrorState,
  SampleBadge,
  SelectRHF,
  TextFieldRHF,
  useSaveShortcut,
  zRequiredText,
} from '@phoenix/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import { WriteNotice } from '../settings/common';
import { SERVICE_CODE } from './CatalogDialogs';
import {
  categoryPath,
  catKeys,
  SavedNotice,
  SERVICE_TYPES,
  SERVICE_UNITS,
  useCategories,
  useSpecialties,
} from './common';

const generalSchema = z.object({
  code: z.string().regex(SERVICE_CODE, { error: 'errors:validation.service_code' }),
  name: zRequiredText(200),
  shortName: z.string().max(30, { error: 'common:validation.too_long' }),
  categoryId: z.string().min(1, { error: 'common:validation.required' }),
  type: z.enum(SERVICE_TYPES as [ServiceType, ...ServiceType[]]),
  unit: z.enum(SERVICE_UNITS as [ServiceUnit, ...ServiceUnit[]]),
  specialtyCodes: z.array(z.string()).max(10, { error: 'common:validation.too_long' }),
  description: z.string().max(2000, { error: 'common:validation.too_long' }),
});
type GeneralForm = z.input<typeof generalSchema>;

export function GeneralTab({ service }: { service: ServiceView }) {
  const { t } = useTranslation('cat');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const write = useWriteAccess('cat.service.manage');
  const categories = useCategories();
  const specialties = useSpecialties();
  const [saved, setSaved] = useState(false);
  const [submitError, setSubmitError] = useState<unknown>();

  const values = useMemo<GeneralForm>(
    () => ({
      code: service.code,
      name: service.name,
      shortName: service.shortName ?? '',
      categoryId: service.categoryId,
      type: service.type,
      unit: service.unit,
      specialtyCodes: [...service.specialtyCodes],
      description: service.description ?? '',
    }),
    [service],
  );
  const { control, handleSubmit, formState, setError } = useForm<GeneralForm>({
    resolver: zodResolver(generalSchema),
    values,
  });

  const save = useMutation({
    mutationFn: (form: GeneralForm) =>
      unwrap(
        tenantApi.PUT('/api/v1/catalog/services/{id}', {
          params: { path: { id: service.id } },
          body: {
            code: form.code.trim(),
            name: form.name,
            shortName: form.shortName.trim() || null,
            categoryId: form.categoryId,
            type: form.type,
            unit: form.unit,
            specialtyCodes: form.specialtyCodes,
            description: form.description.trim() || null,
            duration: null,
            rowVersion: Number(service.rowVersion),
          },
        }),
      ),
    onMutate: () => {
      setSaved(false);
      setSubmitError(undefined);
    },
    onSuccess: () => {
      setSaved(true);
      void queryClient.invalidateQueries({ queryKey: catKeys.all(session?.tenantId) });
    },
    onError: (error) => {
      const applied = applyServerErrors(error, setError, {
        fields: ['code', 'name', 'shortName', 'categoryId', 'type', 'unit', 'specialtyCodes', 'description'],
        codeFields: {
          'PHX-CAT-0201': 'code',
          'PHX-CAT-0202': 'code',
          'PHX-CAT-0105': 'categoryId',
          'PHX-CAT-0205': 'specialtyCodes',
        },
      });
      setSubmitError(applied > 0 ? undefined : error);
    },
  });

  const submit = () => {
    if (write.canWrite) {
      void handleSubmit((form) => save.mutate(form))();
    }
  };
  useSaveShortcut(submit, write.canWrite && !save.isPending);

  const allCategories = categories.data?.items ?? [];
  const names = new Map(specialties.options.map((option) => [option.key, option.name]));

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Box
        component="form"
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          submit();
        }}
      >
        <Stack spacing={2}>
          <WriteNotice write={write} />
          <SavedNotice show={saved && !formState.isDirty} />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextFieldRHF
              control={control}
              name="code"
              labelKey="cat:services.form.code"
              helperKey={service.codeLocked ? 'cat:services.form.code_locked' : 'cat:services.form.code_help'}
              required
              fullWidth
              disabled={service.codeLocked || !write.canWrite}
              slotProps={{ htmlInput: { dir: 'ltr', 'data-testid': 'service-code' } }}
            />
            <TextFieldRHF
              control={control}
              name="name"
              labelKey="cat:services.form.name"
              required
              fullWidth
              disabled={!write.canWrite}
              slotProps={{ htmlInput: { 'data-testid': 'service-name' } }}
            />
            <TextFieldRHF
              control={control}
              name="shortName"
              labelKey="cat:services.form.short_name"
              helperKey="cat:services.form.short_name_help"
              fullWidth
              disabled={!write.canWrite}
            />
          </Stack>
          <SelectRHF
            control={control}
            name="categoryId"
            labelKey="cat:services.form.category"
            required
            fullWidth
            disabled={!write.canWrite}
            options={allCategories.map((item) => ({
              value: item.id,
              label: categoryPath(allCategories, item.id).join(' / '),
            }))}
          />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <SelectRHF
              control={control}
              name="type"
              labelKey="cat:services.form.type"
              required
              fullWidth
              disabled={!write.canWrite}
              options={SERVICE_TYPES.map((value) => ({ value, labelKey: `cat:type.${value}` }))}
            />
            <SelectRHF
              control={control}
              name="unit"
              labelKey="cat:services.form.unit"
              required
              fullWidth
              disabled={!write.canWrite}
              options={SERVICE_UNITS.map((value) => ({ value, labelKey: `cat:unit.${value}` }))}
            />
          </Stack>
          <Stack direction="row" spacing={1} alignItems="flex-start">
            <Controller
              control={control}
              name="specialtyCodes"
              render={({ field, fieldState }) => (
                <Autocomplete
                  multiple
                  fullWidth
                  disabled={!write.canWrite}
                  options={specialties.options.map((option) => option.key)}
                  value={field.value}
                  onChange={(_, value) => field.onChange(value)}
                  getOptionLabel={(key) => names.get(key) ?? key}
                  renderValue={(value, getItemProps) =>
                    value.map((key, index) => {
                      const { key: itemKey, ...itemProps } = getItemProps({ index });
                      return <Chip key={itemKey} size="small" label={names.get(key) ?? key} {...itemProps} />;
                    })
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      name={field.name}
                      label={t('cat:services.form.specialties')}
                      helperText={
                        fieldState.error?.message
                          ? t(fieldState.error.message)
                          : t('cat:services.form.specialties_help')
                      }
                      error={Boolean(fieldState.error)}
                    />
                  )}
                />
              )}
            />
            {specialties.isSample && <SampleBadge />}
          </Stack>
          <TextFieldRHF
            control={control}
            name="description"
            labelKey="cat:services.form.description"
            fullWidth
            multiline
            minRows={2}
            disabled={!write.canWrite}
          />
        </Stack>
        {submitError ? <ErrorState error={submitError} compact /> : null}
        <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
          <Button
            type="submit"
            variant="contained"
            disabled={!write.canWrite || save.isPending}
            data-testid="general-submit"
          >
            {t('common:actions.save')}
          </Button>
        </Stack>
      </Box>
    </Paper>
  );
}
